import { useEffect, useRef, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AudioButton, ExerciseCard, TextInput } from '@fluent/ui';
import type { TranslateTyped as TranslateTypedExercise } from '@fluent/types';
import type { MatchVerdict } from './matchAnswer.js';
import { useAudio } from '../audio/audioPlayer.js';
import './TranslateTyped.css';

export interface TranslateTypedProps {
  exercise: TranslateTypedExercise;
  value: string;
  onChange: (value: string) => void;
  onEnter: () => void;
  /** Disabled once the user has submitted and we're showing a banner. */
  locked: boolean;
  /** Most recent verdict from the reducer — drives the inline `close` nudge. */
  lastVerdict: MatchVerdict | null;
  /** Card kicker override — defaults to the lesson kicker. Lets other
   *  surfaces (flashcard test mode) reuse the renderer with their own copy. */
  kicker?: string;
}

/**
 * TranslateTyped — Georgian prompt on top, single text input below.
 * Controlled: the parent (LessonPlayer) owns the live input value so the
 * Submit bar CTA and the Enter key path both hit the same state.
 */
export function TranslateTyped({
  exercise,
  value,
  onChange,
  onEnter,
  locked,
  lastVerdict,
  kicker,
}: TranslateTypedProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const audio = useAudio(exercise.audio_key);

  // New exercise → refocus input. Value clearing is the parent's job.
  useEffect(() => {
    const id = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => window.cancelAnimationFrame(id);
  }, [exercise.id]);

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onEnter();
    }
  };

  const showCloseHint = lastVerdict === 'close' && !locked;

  return (
    <ExerciseCard
      kicker={kicker ?? t('lesson.translate_typed.kicker')}
      prompt={exercise.prompt}
      promptLang="ka"
    >
      <div className="fluent-typed">
        {exercise.audio_key && (
          <div className="fluent-typed__audio-row">
            <AudioButton
              size="sm"
              playing={audio.playing}
              onClick={() => (audio.playing ? audio.stop() : audio.play())}
              data-testid="typed-audio-btn"
              label={t('lesson.translate_typed.audio_aria')}
            />
            
          </div>
        )}
        <TextInput
          ref={inputRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t('lesson.translate_typed.placeholder')}
          aria-label={t('lesson.translate_typed.aria_input')}
          disabled={locked}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          lang="en"
          data-testid="translate-typed-input"
        />
        {showCloseHint && (
          <p className="fluent-typed__nudge" role="status" aria-live="polite">
            {t('lesson.translate_typed.close_nudge')}
          </p>
        )}
      </div>
    </ExerciseCard>
  );
}
